import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const POSITION_OPTIONS = [
  { label: 'OF', value: 'OF' },
  { label: 'INF', value: 'INF' },
  { label: 'UTL', value: 'UTL' },
  { label: 'LHP', value: 'LHP' },
  { label: 'RHP', value: 'RHP' },
  { label: 'P', value: 'P' },
  { label: 'C', value: 'C' },
  { label: '1B', value: '1B' },
  { label: '2B', value: '2B' },
  { label: '3B', value: '3B' },
  { label: 'SS', value: 'SS' },
  { label: 'LF', value: 'LF' },
  { label: 'CF', value: 'CF' },
  { label: 'RF', value: 'RF' },
];

export const MultiSelectDropdown = ({
  options = POSITION_OPTIONS,
  selected = [],
  onChange,
  label = 'POSITION',
  variant = 'header', // 'header' for table header, 'form' for filter modal
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: variant === 'form' ? rect.left : rect.left + rect.width / 2,
        width: rect.width,
      });
    }
  }, [variant]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updatePosition]);

  const toggleOption = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const clearAll = () => {
    onChange([]);
  };

  const displayLabel = selected.length > 0 ? selected.join(', ') : 'All';

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: variant === 'form' ? pos.left : pos.left,
            transform: variant === 'form' ? 'none' : 'translateX(-50%)',
            width: variant === 'form' ? pos.width : undefined,
            minWidth: 140,
            zIndex: 9999,
          }}
          className="bg-jp-section border border-jp-line rounded-lg shadow-xl py-1 max-h-48 overflow-y-auto"
        >
          {selected.length > 0 && (
            <button
              onClick={clearAll}
              className="w-full text-left px-3 py-1.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-jp-line transition-colors"
            >
              Clear All
            </button>
          )}
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-jp-line cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="w-3.5 h-3.5 rounded border-jp-line text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 bg-jp-section cursor-pointer accent-cyan-500"
              />
              <span className="text-[11px] text-jp-ink font-medium">{opt.label}</span>
            </label>
          ))}
        </div>,
        document.body
      )
    : null;

  // Form variant - matches SimpleDropdown style
  if (variant === 'form') {
    return (
      <>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 text-sm rounded-lg bg-jp-section border border-jp-line text-jp-ink focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 text-left flex items-center justify-between"
        >
          <span className={selected.length > 0 ? 'text-jp-ink truncate' : 'text-jp-muted'}>
            {displayLabel}
          </span>
          <svg className="w-4 h-4 text-jp-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {dropdown}
      </>
    );
  }

  // Header variant - for table headers
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 mx-auto cursor-pointer hover:text-jp-accent-ink transition-colors"
      >
        <span>{label}</span>
        {selected.length > 0 && (
          <span className="bg-cyan-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      {dropdown}
    </>
  );
};

export { POSITION_OPTIONS };
export default MultiSelectDropdown;
